import { Fragment, useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Modal,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import { coreSizeLogsModalStyle as modalStyle } from "./coreSizeStyles";
import { coreSizeLogsModalHeaderCellSx as headerCellSx } from "./coreSizeStyles";

const PRIMARY = "#6148f6";
const EXPANDED_ROW_BG = "rgba(97, 72, 246, 0.08)";
const CATALOG_API_BASE = process.env.REACT_APP_CATALOG_API;
const LOGS_PAGE_SIZE = 10;
const CHILD_LOGS_PAGE_SIZE = 10;

const LOGS_COLUMNS = [
  { header: "ACTIVITY TIME", accessor: "activityTime", minWidth: 180 },
  { header: "CONFIGURATION", accessor: "configuration" },
  { header: "EFFECTIVE CHANGE", accessor: "effectiveChange", minWidth: 200 },
];

function formatLogsActivityTime(rowCreatedAt) {
  if (!rowCreatedAt) return "-";

  const date = new Date(rowCreatedAt);
  if (Number.isNaN(date.getTime())) return "-";

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value;
  const day = getPart("day");
  const month = getPart("month");
  const hour12 = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod");
  return `${hour12}:${minute}${dayPeriod} - ${day} ${month}`;
}

function getCoreSizeOosHitFlag(item) {
  if (item == null) return undefined;

  return item.core_size_OOS_hit_flag;
}

function getRowKey(row) {
  const runId = row?.run_id ?? row?.id;

  if (runId == null) return row?.id;

  const oosHitFlag = getCoreSizeOosHitFlag(row);
  const isDefaultRule = Boolean(row?.is_default_rule);
  return `${runId}-${oosHitFlag ?? 0}-${isDefaultRule ? 1 : 0}`;
}

function isCoreSizeOutOfStock(oosHitFlag) {
  return Number(oosHitFlag ?? 0) !== 0;
}

function isRemovedFromCampaignsPlatform(source) {
  const normalized = String(source || "").toLowerCase();
  return normalized === "flipkart" || normalized === "myntra";
}

function getOutOfStockChildEffectiveChangeText(source) {
  return isRemovedFromCampaignsPlatform(source)
    ? "Product was removed from all campaigns."
    : "Product was paused from all campaigns.";
}

function getOutOfStockParentEffectiveChangeText(count, source) {
  return isRemovedFromCampaignsPlatform(source)
    ? `${count} Products were removed from all campaigns.`
    : `${count} Products were paused from all campaigns.`;
}

function getChildEffectiveChangeText(oosHitFlag, source) {
  return isCoreSizeOutOfStock(oosHitFlag)
    ? getOutOfStockChildEffectiveChangeText(source)
    : "Product was recovered as in stock.";
}

function resolveChildEffectiveChange(item, source) {
  const configurationLabel = item?.configuration_label?.trim();

  if (configurationLabel) {
    const normalized = configurationLabel.toLowerCase();

    if (normalized.includes("recovered")) {
      return "Product was recovered as in stock.";
    }

    if (normalized.includes("out of stock")) {
      return getOutOfStockChildEffectiveChangeText(source);
    }
  }

  return getChildEffectiveChangeText(getCoreSizeOosHitFlag(item), source);
}

function mapParentLogItem(item, index, source) {
  const core_size_OOS_hit_flag = getCoreSizeOosHitFlag(item);

  return {
    id:
      getRowKey({
        run_id: item.run_id,
        core_size_OOS_hit_flag,
        is_default_rule: item.is_default_rule,
      }) || `parent-${index}`,
    run_id: item.run_id,
    activityTime: formatLogsActivityTime(item.row_created_at),
    configuration: item.configuration || "-",
    core_size_OOS_hit_flag,
    is_default_rule: item.is_default_rule,
    effectiveChange: item.effective_change
    ? isCoreSizeOutOfStock(core_size_OOS_hit_flag)
      ? getOutOfStockParentEffectiveChangeText(item.effective_change, source)
      : `${item.effective_change} products recovered as in stock`
    : "-",
    row_created_at: item.row_created_at,
  };
}

function mapChildLogItem(item, index, source) {
  return {
    id: item.product_id ?? item.id ?? `child-${index}`,
    title: item.title ?? "",
    category_rule: item.category_rule ?? "",
    core_size_OOS_hit_flag: getCoreSizeOosHitFlag(item),
    effectiveChange: resolveChildEffectiveChange(item, source),
  };
}

function getCoreSizeLogsUrl(clientId) {
  return `${CATALOG_API_BASE}/client/${clientId}/core-sizes/logs`;
}

async function fetchCoreSizeLogsApi({ clientId, source, ...params }) {
  const { data } = await axios.get(getCoreSizeLogsUrl(clientId), {
    params: { source, ...params },
  });
  return data;
}

function parseLogsCount(result) {
  if (result == null) return 0;

  const { count } = result;

  if (typeof count === "number" && !Number.isNaN(count)) return count;

  if (typeof count === "string" && count !== "") {
    const parsed = Number(count);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (Array.isArray(count) && count[0]?.total != null) {
    const parsed = Number(count[0].total);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (count && typeof count === "object" && count.total != null) {
    const parsed = Number(count.total);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
}

function parseLogsData(result) {
  if (Array.isArray(result)) return result;
  return Array.isArray(result?.data) ? result.data : [];
}

// Converts API literal backslash-n sequences into real line breaks for display.
function normalizeMultilineText(value) {
  if (value == null || value === "" || value === "-") return "";

  const str = typeof value === "string" ? value : String(value);

  return str
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
}

function MultilineTextContent({ value, fallback = "—" }) {
  const normalized = normalizeMultilineText(value);

  if (!normalized) {
    return (
      <Typography variant="body2" color="text.secondary">
        {fallback}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
      {normalized}
    </Typography>
  );
}

function CenteredMessage({ children, minHeight = 120 }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
      minHeight={minHeight}
      py={3}
    >
      {children}
    </Box>
  );
}

function ChildProductCell({ item }) {
  const productId = item?.id != null ? String(item?.id) : "—";

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Avatar
        variant="circular"
        sx={{ width: 40, height: 40, bgcolor: "grey.100", flex: "0 0 auto" }}
      >
        <Inventory2Outlined sx={{ fontSize: 22, color: "grey.400" }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={500} lineHeight={1.3}>
          {item?.title || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {productId}
        </Typography>
      </Box>
    </Stack>
  );
}

function ExpandedChildRows({
  row,
  childRows,
  childLoading,
  childError,
  currentChildPage,
  childTotalCount,
  onChildPageChange,
}) {
  const colSpan = LOGS_COLUMNS.length + 1;
  const rowKey = getRowKey(row);
  const childTotalPages =
    childTotalCount > 0 ? Math.ceil(childTotalCount / CHILD_LOGS_PAGE_SIZE) : 0;
  const showChildPagination = childTotalCount > CHILD_LOGS_PAGE_SIZE;

  if (childLoading) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <CenteredMessage>
            <CircularProgress size={24} sx={{ color: PRIMARY }} />
            <Typography variant="body2" color="text.secondary">
              Loading details...
            </Typography>
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  if (childError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Typography variant="body2" color="error" py={1.5} px={2}>
            {childError}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (!childRows.length) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <CenteredMessage minHeight={80}>
            <Typography variant="body2" color="text.secondary">
              No data found
            </Typography>
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {childRows.map((item, index) => (
        <TableRow
          key={`${rowKey}-${item.id ?? index}`}
          sx={{ bgcolor: EXPANDED_ROW_BG }}
        >
          <TableCell sx={{ width: 48 }} />
          <TableCell sx={{ verticalAlign: "top", width: "26%" }}>
            <ChildProductCell item={item} />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top" }}>
            <MultilineTextContent value={item.category_rule} />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top" }}>
            <Typography variant="body2">{item.effectiveChange}</Typography>
          </TableCell>
        </TableRow>
      ))}
      {showChildPagination && (
        <TableRow sx={{ bgcolor: EXPANDED_ROW_BG }}>
          <TableCell sx={{ width: 48 }} />
          <TableCell colSpan={LOGS_COLUMNS.length}>
            <Box display="flex" justifyContent="flex-end" py={0.5}>
              <CustomPagination
                currentPage={currentChildPage}
                totalPages={childTotalPages}
                onPageChange={onChildPageChange}
                lazyLoading={childLoading}
                totalItems={childTotalCount}
                itemsPerPage={CHILD_LOGS_PAGE_SIZE}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function LogsTableBody({
  logsLoading,
  logsError,
  logsData,
  expanded,
  onToggleRow,
  childLogsMap,
  childLogsLoadingMap,
  childLogsErrorMap,
  childLogsPageMap,
  childLogsTotalCountMap,
  onChildPageChange,
}) {
  const colSpan = LOGS_COLUMNS.length + 1;

  if (logsLoading) {
    return (
      <TableRow sx={{height: 450}}>
        <TableCell colSpan={colSpan}>
          <CenteredMessage>
            <CircularProgress size={24} sx={{ color: PRIMARY }} />
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  if (logsError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <CenteredMessage minHeight={80}>
            <Typography variant="body2" color="text.secondary">
              Error while fetching logs
            </Typography>
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  if (!logsData.length) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <CenteredMessage minHeight={80}>
            <Typography variant="body2" color="text.secondary">
              No logs found
            </Typography>
          </CenteredMessage>
        </TableCell>
      </TableRow>
    );
  }

  return logsData.map((row) => {
    const rowKey = getRowKey(row);
    const isOpen = expanded.has(rowKey);

    return (
      <Fragment key={rowKey}>
        <TableRow hover sx={{ cursor: "pointer" }} onClick={() => onToggleRow(row, rowKey)}>
          <TableCell sx={{ width: 48, py: 0.5 }}>
            <IconButton
              size="small"
              aria-label={isOpen ? "Collapse row" : "Expand row"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleRow(row, rowKey);
              }}
            >
              {isOpen ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
          {LOGS_COLUMNS.map((col) => (
            <TableCell key={col.accessor} sx={{ minWidth: col.minWidth }}>
              {col.accessor === "configuration" ? (
                <MultilineTextContent value={row.configuration} />
              ) : (
                row[col.accessor]
              )}
            </TableCell>
          ))}
        </TableRow>
        {isOpen && (
          <ExpandedChildRows
            row={row}
            childRows={childLogsMap[rowKey] || []}
            childLoading={childLogsLoadingMap[rowKey]}
            childError={childLogsErrorMap[rowKey]}
            currentChildPage={childLogsPageMap[rowKey] ?? 1}
            childTotalCount={childLogsTotalCountMap[rowKey] ?? 0}
            onChildPageChange={(page) => onChildPageChange(row, page)}
          />
        )}
      </Fragment>
    );
  });
}

function CoreSizeLogsModal({ open, onClose, clientId, source }) {
  const [expanded, setExpanded] = useState(new Set());
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [childLogsMap, setChildLogsMap] = useState({});
  const [childLogsLoadingMap, setChildLogsLoadingMap] = useState({});
  const [childLogsErrorMap, setChildLogsErrorMap] = useState({});
  const [childLogsPageMap, setChildLogsPageMap] = useState({});
  const [childLogsTotalCountMap, setChildLogsTotalCountMap] = useState({});

  const logsTotalPages =
    logsTotalCount > 0 ? Math.ceil(logsTotalCount / LOGS_PAGE_SIZE) : 0;
  const showParentPagination =
    !logsLoading && !logsError && logsTotalCount > LOGS_PAGE_SIZE;

  const resetLogsState = useCallback(() => {
    setExpanded(new Set());
    setLogsData([]);
    setLogsLoading(false);
    setLogsError("");
    setLogsPage(1);
    setLogsTotalCount(0);
    setChildLogsMap({});
    setChildLogsLoadingMap({});
    setChildLogsErrorMap({});
    setChildLogsPageMap({});
    setChildLogsTotalCountMap({});
  }, []);

  const fetchCoreSizeLogs = useCallback(
    async (page = 1) => {
      if (!clientId || !source) return;

      try {
        setLogsLoading(true);
        setLogsError("");

        const result = await fetchCoreSizeLogsApi({
          clientId,
          source,
          type: "parent",
          offset: (page - 1) * LOGS_PAGE_SIZE,
          pageSize: LOGS_PAGE_SIZE,
        });

        if (result?.error) {
          setLogsError(result.error || "Failed to fetch logs");
          setLogsData([]);
          setLogsTotalCount(0);
          return;
        }

        setLogsData(parseLogsData(result).map((item, index) => mapParentLogItem(item, index, source)));
        setLogsTotalCount(parseLogsCount(result));
        setLogsPage(page);
      } catch {
        setLogsError("Failed to fetch logs");
        setLogsData([]);
        setLogsTotalCount(0);
      } finally {
        setLogsLoading(false);
      }
    },
    [clientId, source]
  );

  const fetchChildLogs = useCallback(
    async (row, page = 1) => {
      const rowKey = getRowKey(row);

      if (!clientId || !source || !row.run_id) {
        setChildLogsErrorMap((prev) => ({
          ...prev,
          [rowKey]: "Missing run id for child logs",
        }));
        return;
      }

      try {
        setChildLogsLoadingMap((prev) => ({ ...prev, [rowKey]: true }));
        setChildLogsErrorMap((prev) => ({ ...prev, [rowKey]: "" }));

        const result = await fetchCoreSizeLogsApi({
          clientId,
          source,
          type: "child",
          run_id: row.run_id,
          core_size_OOS_hit_flag: getCoreSizeOosHitFlag(row),
          is_default_rule: row.is_default_rule,
          offset: (page - 1) * CHILD_LOGS_PAGE_SIZE,
          pageSize: CHILD_LOGS_PAGE_SIZE,
        });

        if (result?.error) {
          setChildLogsErrorMap((prev) => ({
            ...prev,
            [rowKey]: result.error || "Failed to fetch child logs",
          }));
          return;
        }

        setChildLogsMap((prev) => ({
          ...prev,
          [rowKey]: parseLogsData(result).map((item, index) => mapChildLogItem(item, index, source)),
        }));
        setChildLogsTotalCountMap((prev) => ({
          ...prev,
          [rowKey]: parseLogsCount(result),
        }));
        setChildLogsPageMap((prev) => ({ ...prev, [rowKey]: page }));
      } catch {
        setChildLogsErrorMap((prev) => ({
          ...prev,
          [rowKey]: "Failed to fetch child logs",
        }));
      } finally {
        setChildLogsLoadingMap((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [clientId, source]
  );

  useEffect(() => {
    if (!open) {
      resetLogsState();
    }
  }, [open, resetLogsState]);

  useEffect(() => {
    if (!open || !clientId || !source) return;
    fetchCoreSizeLogs(1);
  }, [open, clientId, source, fetchCoreSizeLogs]);

  const handleToggleRow = useCallback(
    (row, rowKey) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        const isOpening = !next.has(rowKey);

        if (next.has(rowKey)) {
          next.delete(rowKey);
        } else {
          next.add(rowKey);
        }

        if (isOpening) {
          fetchChildLogs(row, 1);
        }

        return next;
      });
    },
    [fetchChildLogs]
  );

  const handleClose = useCallback(
    (event, reason) => {
      onClose?.(event, reason);
    },
    [onClose]
  );

  const handleLogsPageChange = useCallback(
    (page) => {
      setExpanded(new Set());
      fetchCoreSizeLogs(page);
    },
    [fetchCoreSizeLogs]
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          px={2.5}
          py={2}
          borderBottom={1}
          borderColor="divider"
          flexShrink={0}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Configuration Logs
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Activity log of configuration changes and their effective impact on products.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label="Close logs" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box flex={1} overflow="hidden" p={1.25}>
          <TableContainer
            sx={{
              maxHeight: 450,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 48 }} />
                  {LOGS_COLUMNS.map((col) => (
                    <TableCell
                      key={col.accessor}
                      sx={{ ...headerCellSx, minWidth: col.minWidth }}
                    >
                      {col.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <LogsTableBody
                  logsLoading={logsLoading}
                  logsError={logsError}
                  logsData={logsData}
                  expanded={expanded}
                  onToggleRow={handleToggleRow}
                  childLogsMap={childLogsMap}
                  childLogsLoadingMap={childLogsLoadingMap}
                  childLogsErrorMap={childLogsErrorMap}
                  childLogsPageMap={childLogsPageMap}
                  childLogsTotalCountMap={childLogsTotalCountMap}
                  onChildPageChange={fetchChildLogs}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {showParentPagination && (
          <Box
            display="flex"
            justifyContent="flex-end"
            px={2.5}
            py={2}
            borderTop={1}
            borderColor="divider"
            flexShrink={0}
          >
            <CustomPagination
              currentPage={logsPage}
              totalPages={logsTotalPages}
              onPageChange={handleLogsPageChange}
              lazyLoading={logsLoading}
              totalItems={logsTotalCount}
              itemsPerPage={LOGS_PAGE_SIZE}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
}
export default CoreSizeLogsModal;